// =============================================================================
// LIGA AJUPAM — "Leé el reglamento" en los primeros días (v6.11.68 · v6.11.74)
// =============================================================================
// Los primeros días con la app entera habilitada son los que producen los
// errores más caros —cargar el resultado tarde, cancelar sobre la hora, jugar
// sin registrar el partido— y todos están explicados en el reglamento, que casi
// nadie abre. Este CTA aparece durante 5 días y después se va solo.
//
// Cuándo aparece exactamente lo decide `utils/rulebookCtaWindow` (puro,
// `npm run test:rulebook-cta`): cuenta desde que la app quedó ENTERA habilitada
// para esa persona, que para el que se inscribe tarde no es el arranque de la
// liga sino su propia alta.
//
// Sin botón de descartar a propósito: la ventana ya es corta y acotada, y el
// pedido es justamente que no pase desapercibido. Tampoco bloquea nada — el
// modal bloqueante es otra cosa (`RulebookReacceptanceGate`, que sólo se activa
// cuando cambia la versión publicada).
//
// -----------------------------------------------------------------------------
// v6.11.74 — El Simulacro entra acá adentro
// -----------------------------------------------------------------------------
// El onboarding lúdico (docs/features/ONBOARDING_SIMULACRO.md) se ofrece como un
// botón dentro de esta misma tarjeta y no como una carta aparte. Van juntos
// porque dicen lo mismo con dos tonos: "leé el reglamento" es el pedido serio,
// "probá un partido de mentira" es la puerta de entrada para el que no lo va a
// leer igual. Dos tarjetas separadas compitiendo en el Inicio se anulaban.
//
// OJO CON LAS DOS VIGENCIAS, que no son la misma y es el error fácil de cometer
// acá: el pedido de leer el reglamento dura 5 días y se va solo; el simulacro
// dura hasta que la persona lo hace. Por eso la tarjeta se muestra si vale
// CUALQUIERA de las dos, y cada mitad se muestra por su cuenta. Anidar el
// simulacro dentro de la ventana de 5 días lo haría desaparecer sin que nadie
// lo haya jugado.
//
// -----------------------------------------------------------------------------
// v6.11.94 — Un interruptor por mitad
// -----------------------------------------------------------------------------
// `rulebook_cta_enabled` apaga el aviso del reglamento y `simulacro_cta_enabled`
// el botón del simulacro, cada uno por su cuenta (Ajustes → Fechas Clave).
// Hasta v6.11.91 había uno solo y apagaba las dos mitades: no por gusto, sino
// porque apagar sólo la de arriba dejaba un botón lima en el Inicio que el admin
// no podía sacar de ningún lado. Con dos columnas ese callejón no existe.
//
// Ninguno de los dos toca el Centro de Ayuda, donde el simulacro se puede
// repetir siempre (v6.11.76): lo que se apaga acá es el EMPUJÓN del Inicio.

import React from 'react';
import { BookOpen, ChevronRight, FlaskConical } from 'lucide-react';
import { useRulebookCtaWindow } from '../hooks/useRulebookCtaWindow';

interface RulebookCtaProps {
  /** `regular_phase_start_date` de la liga del equipo. */
  regularPhaseStartDate?: string | null;
  /** `teams.created_at` — cuándo se inscribió esta pareja. */
  teamCreatedAt?: string | null;
  /** true mientras Tabla/Partidos sigan candadeadas (utils/sectionAccess). */
  seasonSectionsLocked?: boolean | null;
  /** false mientras settings/equipo no llegaron. */
  dataLoaded: boolean;
  /** Slug de la liga: el reglamento vive en /liga/<slug>/reglamento. */
  leagueSlug?: string | null;
  /** Fallback cuando no hay slug (mismo criterio que HelpDrawer). */
  regulationsUrl?: string | null;
  /** `rulebook_cta_enabled` de la liga. false apaga el aviso del reglamento. */
  ctaEnabled?: boolean | null;
  /** `rulebook_cta_start_date`. Con esta o la de fin, manda la ventana del admin. */
  ctaStartDate?: string | null;
  /** `rulebook_cta_end_date`, inclusive. */
  ctaEndDate?: string | null;
  /** `simulacro_cta_enabled` de la liga. false apaga el botón del simulacro. */
  simulacroCtaEnabled?: boolean | null;
  /** true si esta persona todavía no hizo el simulacro (useSimulacroGate). */
  simulacroPending?: boolean;
  /** Abre el simulacro. Sin esto, el botón no se muestra. */
  onOpenSimulacro?: () => void;
}

export const RulebookCta: React.FC<RulebookCtaProps> = ({
  regularPhaseStartDate,
  teamCreatedAt,
  seasonSectionsLocked,
  dataLoaded,
  leagueSlug,
  regulationsUrl,
  ctaEnabled,
  ctaStartDate,
  ctaEndDate,
  simulacroCtaEnabled,
  simulacroPending = false,
  onOpenSimulacro,
}) => {
  // El "ahora" lo congela el hook (render puro) y lo refresca al volver del
  // segundo plano, que es cuando pudo cruzarse la medianoche.
  const ctaWindow = useRulebookCtaWindow({
    regularPhaseStartDate,
    teamCreatedAt,
    seasonSectionsLocked,
    ctaEnabled,
    ctaStartDate,
    ctaEndDate,
    dataLoaded,
  });

  // Mismo criterio de link que HelpDrawer: el reglamento propio de la edición si
  // hay slug, y si no el PDF/URL configurado.
  const href = leagueSlug ? `/liga/${leagueSlug}/reglamento` : regulationsUrl;

  // v6.11.94 — Cada mitad tiene su interruptor. Antes había uno solo y apagaba
  // la tarjeta entera: era la única forma de que apagar el aviso no dejara un
  // botón lima en el Inicio imposible de sacar. Con dos columnas
  // (`rulebook_cta_enabled` / `simulacro_cta_enabled`) eso deja de ser un
  // problema y el admin puede querer una mitad sin la otra.
  //
  // `ctaWindow.visible` ya vale false cuando el aviso está apagado (mode 'off'),
  // así que no hace falta un early return: si las dos mitades quedan en false,
  // la tarjeta no se renderiza igual, tres líneas más abajo.
  const showRulebook = ctaWindow.visible && !!href;
  const showSimulacro = simulacroCtaEnabled !== false && simulacroPending && !!onOpenSimulacro;

  if (!showRulebook && !showSimulacro) return null;

  return (
    <div className="mb-4 rounded-2xl border-2 border-primary/30 bg-primary/5 p-4">
      {showRulebook && href && (
        <a
          href={href}
          target={leagueSlug ? undefined : '_blank'}
          rel={leagueSlug ? undefined : 'noopener noreferrer'}
          className="block active:scale-[0.99] transition-transform"
        >
          <div className="flex items-start gap-3">
            <div className="bg-primary text-white p-2 rounded-xl shrink-0">
              <BookOpen size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-display font-black uppercase text-sm text-primary tracking-tight">
                Por favor, leé el reglamento
              </p>
              <p className="text-sm text-text-main leading-snug mt-1">
                Es corto y es lo que evita los errores que cuestan puntos: los plazos para cargar el
                resultado, cómo se cancela un partido y cómo funcionan las advertencias.
              </p>
              <span className="inline-flex items-center gap-0.5 text-xs font-bold text-primary mt-2">
                Abrir el reglamento <ChevronRight size={14} />
              </span>
            </div>
          </div>
        </a>
      )}

      {showSimulacro && (
        <>
          {/* Cuando las dos mitades conviven, el simulacro se presenta como la
              alternativa al documento — de ahí el "¿No tenés ganas de leerlo?". */}
          {showRulebook && (
            <div className="flex items-center gap-2.5 my-3.5">
              <div className="h-px flex-1 bg-primary/15" />
              <span className="text-[10px] font-black uppercase tracking-widest text-primary/50">
                ¿No tenés ganas de leerlo?
              </span>
              <div className="h-px flex-1 bg-primary/15" />
            </div>
          )}

          {/* v6.11.91 — Solo, el botón no se explica. Las dos vigencias no son la
              misma (ver cabecera): el pedido de leer el reglamento dura 5 días y
              se va, el simulacro dura hasta que la persona lo juega, así que la
              mayoría de las veces este botón queda SIN la mitad de arriba. Sin
              este bloque lo único que se lee es "Probá un partido de mentira",
              que no dice de qué se trata ni para qué sirve. */}
          {!showRulebook && (
            <div className="mb-3">
              <p className="font-display font-black uppercase text-sm text-primary tracking-tight">
                ¿Sabés cómo funciona la liga?
              </p>
              <p className="text-sm text-text-main leading-snug mt-1">
                El simulacro es el reglamento hecho juego: un partido entero —arreglarlo con el
                rival, cargarlo, jugarlo y reportar el resultado— donde en cada paso decidís vos.
                Son las mismas decisiones que en la cancha cuestan puntos, pero acá no afectan a tu
                equipo ni a la tabla.
              </p>
            </div>
          )}

          <button
            onClick={onOpenSimulacro}
            className="w-full flex items-center gap-3 rounded-xl bg-secondary border-2 border-secondary-dark/30 px-4 py-3 text-left active:scale-[0.98] transition-transform shadow-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-dark"
          >
            {/* `mt-0.5` alinea el ícono con la primera línea del título en vez de
                centrarlo contra las dos líneas del bloque. */}
            <FlaskConical size={22} className="text-secondary-dark shrink-0 self-start mt-0.5" />
            <span className="min-w-0 flex-1">
              <span className="block font-display font-black uppercase text-sm text-secondary-dark tracking-tight leading-none">
                Probá un partido de mentira
              </span>
              <span className="block text-[13px] text-secondary-dark/80 leading-snug mt-1">
                5 minutos. Equivocate acá y no en la cancha.
              </span>
            </span>
            <ChevronRight size={18} className="text-secondary-dark shrink-0" />
          </button>
        </>
      )}
    </div>
  );
};
